import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiTrendingUp } from 'react-icons/fi';
import { useMarketContext } from '../../contexts/MarketContext';
import { Badge } from '../common/Badge';
import { getTeamLogo } from '../../utils/teamLogos';
import { formatGameTimeLabel } from '../../utils/formatters';
import { Fire } from '@phosphor-icons/react';

// Get short team name (last word or abbreviation)
function getShortName(teamName) {
  if (!teamName) return '';
  const parts = teamName.split(' ');
  return parts[parts.length - 1]; // Return last word (e.g., "Lakers" from "Los Angeles Lakers")
}

export function AIPicks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { markets, predictions } = useMarketContext();

  // AI Picks - markets with positive EV/edge and isAISelected, sorted by edge, max 2 (like original site)
  const aiPicks = markets
    .filter((m) => {
      const pred = predictions[m.id];
      if (!pred) return false;
      // 使用 isAISelected 或者 edge > 0 来判断是否是 AI 精选
      return pred.isAISelected || (pred.edge && pred.edge > 0) || (pred.ev && pred.ev > 0);
    })
    .sort((a, b) => {
      const edgeA = predictions[a.id]?.edge || predictions[a.id]?.ev || 0;
      const edgeB = predictions[b.id]?.edge || predictions[b.id]?.ev || 0;
      return edgeB - edgeA;
    })
    .slice(0, 2);

  if (aiPicks.length === 0) return null;

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Header */}
      <div className="glass-card px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fire size={20} weight="duotone" className="text-accent-warm" />
          <span className="text-white font-bold tracking-wide">{t('leftSidebar.aiPicks')}</span>
        </div>
        <span className="text-xs font-bold text-white bg-white/10 px-2.5 py-1 rounded-lg">
          {aiPicks.length}
        </span>
      </div>

      {/* AI Picks List */}
      <div className="space-y-3">
        {aiPicks.map((market) => {
          const pred = predictions[market.id];
          return (
            <div
              key={market.id}
              onClick={() => navigate(`/market/${market.id}`)}
              className="glass-card p-3 cursor-pointer hover:bg-glass-hover hover:border-accent/30 group transition-all duration-300"
            >
              {/* Teams Row */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center -space-x-2">
                  <img
                    src={getTeamLogo(market.teamA)}
                    alt={market.teamA}
                    className="w-8 h-8 rounded-full bg-surface-highlight border-2 border-surface p-1 z-10"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <img
                    src={getTeamLogo(market.teamB)}
                    alt={market.teamB}
                    className="w-8 h-8 rounded-full bg-surface-highlight border-2 border-surface p-1 z-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-bold text-gray-100 truncate group-hover:text-white transition-colors">
                    {getShortName(market.teamA)} vs {getShortName(market.teamB)}
                  </span>
                  <span className="text-[10px] text-gray-500">
                    {formatGameTimeLabel(market.gameTime, {
                      todayLabel: t('market.today'),
                      tomorrowLabel: t('market.tomorrow'),
                    }) || '-'}
                  </span>
                </div>
              </div>

              {/* AI Prediction */}
              <div className="bg-surface-highlight/40 rounded-lg p-2.5 flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Confidence</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-white text-shadow-sm">
                      {Math.round((pred?.prob || pred?.probability || 0.5) * 100)}%
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-accent"></span>
                  </div>
                </div>

                <Badge variant="positive" className="text-xs shadow-[0_6px_14px_rgba(8,12,18,0.3)] border border-positive/20">
                  <FiTrendingUp className="mr-1" />
                  +{(pred?.edge || pred?.ev || 0).toFixed(1)}% EV
                </Badge>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
